import React, { useState, useEffect } from 'react';
import { ArrowLeft, ChevronRight, Info, ShieldCheck, ShieldAlert, Calculator } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { formatPrice } from '../../utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function StyleInfoTooltip({ colors }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="absolute top-1.5 right-1.5">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center justify-center"
        style={{ color: colors.secondaryText }}
      >
        <Info className="w-3 h-3" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute bottom-full right-0 z-50 mb-2 w-64 rounded-xl p-3 shadow-xl text-left"
            style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderWidth: '1px', borderColor: colors.accentBorder }}>
            <p className="text-[12px] font-semibold mb-1.5" style={{ color: colors.primaryText }}>
              System recommendation · doesn't affect your score
            </p>
            <p className="text-[11px] mb-2" style={{ color: colors.secondaryText }}>
              Assigned based on your risk profile + experience level:
            </p>
            <div className="space-y-1">
              {[
                ['Long-term',  'Very Conservative + Beginner'],
                ['Positional', 'Very Conservative (intermediate+), Conservative + Beginner'],
                ['Swing',      'Conservative (intermediate+), Moderate, Aggressive (beginner)'],
                ['Your pick',  'Moderate / Aggressive + experienced — follows your preference'],
              ].map(([style, when]) => (
                <div key={style} className="flex gap-1.5">
                  <span className="text-[11px] font-semibold flex-shrink-0 w-16" style={{ color: colors.accent }}>{style}</span>
                  <span className="text-[11px]" style={{ color: colors.secondaryText }}>{when}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function PositionSizer({ riskProfile, user, colors }) {
  const accent    = colors.accent;
  const balance   = user?.virtual_balance || 1000000;
  const myType    = riskProfile?.risk_profile_type;

  const ALL_PROFILES = [
    { type: 'Very Conservative', short: 'V.Cons',  pct: 0.5, trades: 1, optionsAllocPct: 5.0  },
    { type: 'Conservative',      short: 'Cons',    pct: 1.0, trades: 2, optionsAllocPct: 8.0  },
    { type: 'Moderate',          short: 'Mod',     pct: 1.5, trades: 3, optionsAllocPct: 12.0 },
    { type: 'Aggressive',        short: 'Aggr',    pct: 2.0, trades: 5, optionsAllocPct: 18.0 },
    { type: 'Very Aggressive',   short: 'V.Aggr',  pct: 2.5, trades: 8, optionsAllocPct: 25.0 },
  ];

  const [priceInput, setPriceInput]     = useState('');
  const [selectedType, setSelectedType] = useState(myType || 'Moderate');
  const [allStocks, setAllStocks]       = useState([]);
  const [activeStock, setActiveStock]   = useState(null);
  const [searchTerm, setSearchTerm]     = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => { if (myType) setSelectedType(myType); }, [myType]);

  useEffect(() => {
    fetch(`${API_URL}/stocks`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setAllStocks(data))
      .catch(() => {});
  }, []);

  const filteredStocks = searchTerm.length > 0
    ? allStocks.filter(s =>
        s.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (s.company_name || '').toLowerCase().includes(searchTerm.toLowerCase())
      ).slice(0, 6)
    : [];

  const selectStock = (stock) => {
    setActiveStock(stock);
    setSearchTerm('');
    setShowDropdown(false);
    if (stock.current_price) setPriceInput(String(parseFloat(stock.current_price).toFixed(2)));
  };

  const clearStock = () => {
    setActiveStock(null);
    setPriceInput('');
    setSearchTerm('');
  };

  const price           = parseFloat(priceInput) || 0;
  const selected        = ALL_PROFILES.find(p => p.type === selectedType) || ALL_PROFILES[2];
  const maxTrade        = (balance * selected.pct) / 100;
  const shares          = price > 0 ? Math.floor(maxTrade / price) : 0;
  const maxDay          = maxTrade * selected.trades;
  const move2           = shares * price * 0.02;
  const balancePct      = balance > 0 ? ((move2 / balance) * 100).toFixed(2) : '0';
  const activeOptAllocPct = (selectedType === myType && riskProfile?.options_allocation_pct != null)
    ? riskProfile.options_allocation_pct
    : selected.optionsAllocPct;
  const optionsAllocAmt = (balance * activeOptAllocPct) / 100;

  const selectedIdx  = ALL_PROFILES.findIndex(p => p.type === selectedType);
  const nextProfile  = selectedIdx < ALL_PROFILES.length - 1 ? ALL_PROFILES[selectedIdx + 1] : null;
  const nextShares   = nextProfile && price > 0 ? Math.floor(((balance * nextProfile.pct) / 100) / price) : 0;
  const savedOnLoss  = nextProfile ? (nextShares - shares) * price * 0.02 : 0;

  return (
    <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${accent}35` }}>
      {/* Header */}
      <div className="px-4 py-2.5" style={{ backgroundColor: colors.inputBg, borderBottom: `1px solid ${colors.border}` }}>
        <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: colors.secondaryText }}>
          Position Sizer
        </span>
      </div>

      <div className="p-4 space-y-4" style={{ background: colors.cardBg }}>

        {/* Stock search */}
        <div className="relative">
          <p className="text-[10px] font-semibold uppercase tracking-wide mb-1.5" style={{ color: colors.secondaryText }}>
            Stock
          </p>
          {activeStock ? (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl"
              style={{ backgroundColor: colors.inputBg, border: `1px solid ${accent}55` }}>
              <div className="flex-1 min-w-0">
                <span className="text-[13px] font-bold" style={{ color: colors.primaryText }}>
                  {activeStock.symbol.replace('.NS', '')}
                </span>
                <span className="text-[11px] ml-2" style={{ color: colors.secondaryText }}>
                  {activeStock.company_name}
                </span>
              </div>
              <button onClick={clearStock} className="flex-shrink-0 text-[11px] px-2 py-0.5 rounded-md"
                style={{ background: `${accent}18`, color: accent }}>
                ✕
              </button>
            </div>
          ) : (
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setShowDropdown(true); }}
                onFocus={() => setShowDropdown(true)}
                onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                className="w-full px-3 py-2.5 rounded-xl text-[13px] outline-none"
                style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.border}`, color: colors.primaryText }}
                placeholder="Search stock e.g. Reliance, Tata Steel…"
              />
              {showDropdown && filteredStocks.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-1 z-50 rounded-xl overflow-hidden shadow-xl"
                  style={{ background: colors.cardBg, border: `1px solid ${colors.border}` }}>
                  {filteredStocks.map(s => (
                    <button
                      key={s.symbol}
                      onMouseDown={() => selectStock(s)}
                      className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-opacity-80 transition-colors"
                      style={{ borderBottom: `1px solid ${colors.border}` }}>
                      <div className="min-w-0">
                        <span className="text-[12px] font-bold" style={{ color: colors.primaryText }}>
                          {s.symbol.replace('.NS', '')}
                        </span>
                        <span className="text-[11px] ml-2 truncate" style={{ color: colors.secondaryText }}>
                          {s.company_name}
                        </span>
                      </div>
                      {s.current_price && (
                        <span className="text-[11px] font-semibold flex-shrink-0 ml-2" style={{ color: accent }}>
                          ₹{parseFloat(s.current_price).toFixed(0)}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Stock price input */}
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide mb-1.5" style={{ color: colors.secondaryText }}>
            Price per Share
          </p>
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl"
            style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.border}` }}>
            <span className="text-[15px] font-bold flex-shrink-0" style={{ color: colors.secondaryText }}>₹</span>
            <input
              type="number"
              min="1"
              value={priceInput}
              onChange={e => setPriceInput(e.target.value)}
              className="flex-1 bg-transparent text-[15px] font-bold outline-none"
              style={{ color: colors.primaryText }}
              placeholder="Enter price…"
            />
            {price > 0 && (
              <span className="text-[10px] flex-shrink-0" style={{ color: colors.secondaryText }}>per share</span>
            )}
          </div>
        </div>

        {/* Profile selector */}
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide mb-1.5" style={{ color: colors.secondaryText }}>
            Profile
            {myType && selectedType !== myType && (
              <button
                onClick={() => setSelectedType(myType)}
                className="ml-2 text-[10px] font-semibold"
                style={{ color: accent }}>
                (reset to yours)
              </button>
            )}
          </p>
          <div className="flex gap-1.5">
            {ALL_PROFILES.map(p => {
              const isSelected = p.type === selectedType;
              const isMe       = p.type === myType;
              return (
                <button
                  key={p.type}
                  onClick={() => setSelectedType(p.type)}
                  className="flex-1 py-1.5 rounded-lg text-[11px] font-semibold transition-all"
                  style={{
                    background:   isSelected ? accent : `${accent}10`,
                    color:        isSelected ? '#fff' : colors.secondaryText,
                    border:       isMe && !isSelected ? `1px solid ${accent}55` : '1px solid transparent',
                  }}
                >
                  {p.short}
                  {isMe && <span style={{ fontSize: 6, verticalAlign: 'super', marginLeft: 1 }}>●</span>}
                </button>
              );
            })}
          </div>
          <p className="text-[10px] mt-1.5" style={{ color: colors.secondaryText }}>
            ● = your profile · {selected.pct}% of {formatPrice(balance)} balance · {selected.trades} trade{selected.trades > 1 ? 's' : ''}/day max
          </p>
        </div>

        {/* Key metrics */}
        <div className="grid grid-cols-2 gap-2">
          {[
            { label: 'Max / Trade',      value: formatPrice(maxTrade),               sub: `${selected.pct}% of balance` },
            { label: 'Shares',           value: shares > 0 ? `${shares}` : price > 0 ? '< 1' : '—', sub: price > 0 ? `at ₹${price}` : 'enter price' },
            { label: 'Max / Day',        value: formatPrice(maxDay),                  sub: `${selected.trades} × max/trade` },
            { label: 'Options Premium',  value: formatPrice(optionsAllocAmt),         sub: `${activeOptAllocPct}% of capital`, accent: '#a855f7' },
          ].map(({ label, value, sub, accent: tileAccent }) => (
            <div key={label} className="rounded-xl p-2.5 text-center"
              style={{ background: `${tileAccent || accent}0e`, border: `1px solid ${tileAccent || accent}20` }}>
              <p className="text-[13px] font-black leading-tight" style={{ color: colors.primaryText }}>{value}</p>
              <p className="text-[9px] font-bold uppercase tracking-wide mt-0.5" style={{ color: tileAccent || accent }}>{label}</p>
              <p className="text-[9px] mt-0.5" style={{ color: colors.secondaryText }}>{sub}</p>
            </div>
          ))}
        </div>

        {/* All profiles comparison table */}
        <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${colors.border}` }}>
          <div className="px-3 py-1.5"
            style={{ backgroundColor: colors.inputBg, display: 'grid', gridTemplateColumns: '2fr 1.4fr 0.9fr 1.5fr' }}>
            {['Profile', 'Max/Trade', 'Shares', 'Max/Day'].map(h => (
              <p key={h} className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: colors.secondaryText }}>{h}</p>
            ))}
          </div>
          {ALL_PROFILES.map((p, i) => {
            const mt  = (balance * p.pct) / 100;
            const sh  = price > 0 ? Math.floor(mt / price) : 0;
            const isSelected = p.type === selectedType;
            const isMe       = p.type === myType;
            return (
              <button
                key={p.type}
                onClick={() => setSelectedType(p.type)}
                className="w-full text-left px-3 py-2"
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1.4fr 0.9fr 1.5fr',
                  alignItems: 'center',
                  backgroundColor: isSelected ? `${accent}18` : isMe ? `${accent}08` : colors.cardBg,
                  borderTop: i > 0 ? `1px solid ${colors.border}` : 'none',
                  outline: 'none',
                }}>
                <div className="min-w-0">
                  <div className="flex items-center gap-1">
                    {isSelected && <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: accent }} />}
                    {!isSelected && isMe && <div className="w-1.5 h-1.5 rounded-full flex-shrink-0 opacity-50" style={{ backgroundColor: accent }} />}
                    <p className="text-[11px] font-semibold leading-tight truncate"
                      style={{ color: isSelected ? accent : colors.primaryText }}>{p.short}</p>
                  </div>
                  <p className="text-[10px]" style={{ color: colors.secondaryText }}>{p.pct}%</p>
                </div>
                <p className="text-[11px] font-bold" style={{ color: isSelected ? accent : colors.primaryText }}>{formatPrice(mt)}</p>
                <p className="text-[11px] font-bold" style={{ color: isSelected ? accent : colors.primaryText }}>
                  {price > 0 ? (sh > 0 ? `${sh}` : '< 1') : '—'}
                </p>
                <p className="text-[11px]" style={{ color: colors.secondaryText }}>{formatPrice(mt * p.trades)}</p>
              </button>
            );
          })}
        </div>

        {/* P&L scenarios */}
        {shares > 0 && (
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-xl p-3" style={{ background: '#16C78412', border: '1px solid #16C78430' }}>
              <p className="text-[10px] font-semibold" style={{ color: '#16C784' }}>+2% move</p>
              <p className="text-[18px] font-black" style={{ color: '#16C784' }}>+{formatPrice(move2)}</p>
              <p className="text-[9px] mt-0.5" style={{ color: '#16C78470' }}>
                {shares} sh. × ₹{(price * 0.02).toFixed(0)}
              </p>
            </div>
            <div className="rounded-xl p-3" style={{ background: '#ef444412', border: '1px solid #ef444430' }}>
              <p className="text-[10px] font-semibold" style={{ color: '#ef4444' }}>-2% move</p>
              <p className="text-[18px] font-black" style={{ color: '#ef4444' }}>-{formatPrice(move2)}</p>
              <p className="text-[9px] mt-0.5" style={{ color: '#ef444470' }}>
                {balancePct}% of your balance
              </p>
            </div>
          </div>
        )}

        {/* Savings vs one step aggressive */}
        {nextProfile && savedOnLoss > 0 && shares > 0 && (
          <div className="flex items-center justify-between rounded-xl px-3 py-2.5"
            style={{ background: '#16C78408', border: '1px solid #16C78428' }}>
            <p className="text-[11px]" style={{ color: colors.secondaryText }}>
              vs <span style={{ color: colors.primaryText, fontWeight: 600 }}>{nextProfile.short}</span> on a -2% move
            </p>
            <p className="text-[12px] font-bold" style={{ color: '#16C784' }}>you save {formatPrice(savedOnLoss)}</p>
          </div>
        )}

      </div>
    </div>
  );
}

export default function RiskProfileSection({ onBack, locked = false }) {
  const { colors } = useTheme();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [riskProfile, setRiskProfile] = useState(null);
  const [riskStep, setRiskStep] = useState(null);
  const [riskStepSaving, setRiskStepSaving] = useState(false);
  const [riskReturnTo, setRiskReturnTo] = useState(null);
  const [adherence, setAdherence] = useState(null);
  const [riskForm, setRiskForm] = React.useState({
    age_group: '', monthly_income: '', income_allocation_pct: '', loan_burden: '', dependents: '',
    trading_experience: '', ta_knowledge: '',
    capital_source: '', max_loss_tolerance: '', losing_trade_hold: '',
    loss_reaction: '', trading_goal: '', consecutive_loss_action: '',
  });

  const loadProfile = async () => {
    await fetchRiskProfile();
    axios.get(`${API_URL}/behavioral-health`)
      .then(r => setAdherence(r.data?.risk ?? null))
      .catch(() => {});
  };

  useEffect(() => { loadProfile(); }, []);

  const fetchRiskProfile = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/profile`);
      if (res.data && Object.keys(res.data).length > 0) {
        setRiskProfile(res.data);
        setRiskStep(res.data.questionnaire_completed ? 'score' : 0);
        setRiskForm({
          age_group: res.data.age_group || '',
          monthly_income: res.data.monthly_income || '',
          income_allocation_pct: res.data.income_allocation_pct || '',
          loan_burden: res.data.loan_burden || '',
          dependents: res.data.dependents ?? '',
          trading_experience: res.data.trading_experience || '',
          ta_knowledge: res.data.ta_knowledge || '',
          capital_source: res.data.capital_source || '',
          max_loss_tolerance: res.data.max_loss_tolerance || '',
          losing_trade_hold: res.data.losing_trade_hold || '',
          loss_reaction: res.data.loss_reaction || '',
          trading_goal: res.data.trading_goal || '',
          consecutive_loss_action: res.data.consecutive_loss_action || '',
        });
      } else {
        setRiskStep(0);
      }
    } catch (e) { setRiskStep(0); }
  };

    const CARDS = [
      // ── CAPACITY (5) ─────────────────────────────────────────────────────
      {
        field: 'age_group', category: 'Financial Capacity', step: '1 of 5',
        question: 'How old are you?',
        options: [
          { value: '<25',   label: 'Under 25',  sub: 'More time to recover losses' },
          { value: '25-34', label: '25 – 34' },
          { value: '35-50', label: '35 – 50' },
          { value: '>50',   label: 'Over 50',   sub: 'Closer to retirement' },
        ],
      },
      {
        field: 'monthly_income', category: 'Financial Capacity', step: '2 of 5',
        question: 'What is your monthly income?',
        options: [
          { value: '>1L',     label: 'Above ₹1 Lakh' },
          { value: '50K-1L',  label: '₹50K – ₹1 Lakh' },
          { value: '25K-50K', label: '₹25K – ₹50K' },
          { value: '<25K',    label: 'Below ₹25K' },
        ],
      },
      {
        field: 'income_allocation_pct', category: 'Financial Capacity', step: '3 of 5',
        question: 'What % of income goes towards trading?',
        options: [
          { value: '>40',   label: 'More than 40%', sub: 'Heavy allocation' },
          { value: '20-40', label: '20 – 40%' },
          { value: '10-20', label: '10 – 20%' },
          { value: '<10',   label: 'Less than 10%', sub: 'Conservative allocation' },
        ],
      },
      {
        field: 'loan_burden', category: 'Financial Capacity', step: '4 of 5',
        question: 'Do you have active loans or EMIs?',
        options: [
          { value: 'none', label: 'No loans',              sub: 'Completely debt-free' },
          { value: '<50',  label: 'EMI < 50% of income',   sub: 'Manageable burden' },
          { value: '>50',  label: 'EMI > 50% of income',   sub: 'High burden' },
        ],
      },
      {
        field: 'dependents', category: 'Financial Capacity', step: '5 of 5',
        question: 'How many people depend on your income?',
        options: [
          { value: 0, label: 'None',        sub: 'Just me' },
          { value: 1, label: '1 person' },
          { value: 2, label: '2 people' },
          { value: 3, label: '3 or more',   sub: 'Multiple dependents' },
        ],
      },
      // ── KNOWLEDGE (2) ────────────────────────────────────────────────────
      {
        field: 'trading_experience', category: 'Knowledge & Experience', step: '1 of 2',
        question: 'How long have you been trading?',
        options: [
          { value: '3+yrs', label: '3+ years',            sub: 'Experienced trader' },
          { value: '1-3yrs', label: '1 – 3 years' },
          { value: '<1yr',  label: 'Less than 1 year' },
          { value: 'never', label: 'Never traded',         sub: 'Complete beginner' },
        ],
      },
      {
        field: 'ta_knowledge', category: 'Knowledge & Experience', step: '2 of 2',
        question: 'How well do you understand technical analysis?',
        options: [
          { value: 'expert',   label: 'Expert',           sub: 'Charts, indicators, patterns' },
          { value: 'basic',    label: 'Basic',            sub: 'RSI, Moving Averages' },
          { value: 'learning', label: 'Still learning',   sub: 'Know the basics' },
          { value: 'none',     label: 'No knowledge',     sub: 'Starting from scratch' },
        ],
      },
      // ── BEHAVIOR (6) ─────────────────────────────────────────────────────
      {
        field: 'capital_source', category: 'Behavioral Traits', step: '1 of 6',
        question: 'Where does your trading capital come from?',
        options: [
          { value: 'surplus',         label: 'Pure surplus savings',     sub: "Money I don't need at all" },
          { value: 'monthly_savings', label: 'Part of monthly savings',  sub: 'Planned allocation' },
          { value: 'borrowed',        label: 'Borrowed / loan',          sub: 'Not my own money' },
        ],
      },
      {
        field: 'max_loss_tolerance', category: 'Behavioral Traits', step: '2 of 6',
        question: 'Max loss you can accept per trade?',
        options: [
          { value: '2-5', label: '2% – 5%',         sub: 'Moderate loss tolerance' },
          { value: '1-2', label: '1% – 2%',         sub: 'Controlled risk' },
          { value: '>5',  label: 'More than 5%',    sub: 'High risk appetite' },
          { value: '<1',  label: 'Less than 1%',    sub: 'Very low tolerance' },
        ],
      },
      {
        field: 'losing_trade_hold', category: 'Behavioral Traits', step: '3 of 6',
        question: 'How long do you hold a losing trade?',
        options: [
          { value: 'same_day', label: 'Exit same day',     sub: 'Cut losses fast' },
          { value: '2-3days',  label: '2 – 3 days',        sub: 'Give it some room' },
          { value: '1week',    label: 'Up to a week',      sub: 'Patient holder' },
          { value: 'forever',  label: 'Until it recovers', sub: 'Never exit at loss' },
        ],
      },
      {
        field: 'loss_reaction', category: 'Behavioral Traits', step: '4 of 6',
        question: 'When your stop-loss is hit, you…',
        options: [
          { value: 'strict_sl',   label: 'Exit immediately',    sub: 'Strict SL discipline' },
          { value: 'review_exit', label: 'Review, then exit',   sub: 'Validate before closing' },
          { value: 'hold',        label: 'Hold and hope',       sub: 'Wait for recovery' },
          { value: 'avg_down',    label: 'Average down',        sub: 'Buy more to reduce cost' },
        ],
      },
      {
        field: 'trading_goal', category: 'Behavioral Traits', step: '5 of 6',
        question: 'What is your primary trading goal?',
        options: [
          { value: 'wealth',   label: 'Long-term wealth',   sub: 'Compound over years' },
          { value: 'income',   label: 'Regular income',     sub: 'Monthly cash flow' },
          { value: 'learning', label: 'Learning skills',    sub: 'Build knowledge first' },
        ],
      },
      {
        field: 'consecutive_loss_action', category: 'Behavioral Traits', step: '6 of 6',
        question: 'After 3 consecutive losses, you…',
        options: [
          { value: 'stop',   label: 'Stop trading',        sub: 'Take a break and reflect' },
          { value: 'reduce', label: 'Reduce size',         sub: 'Tighten up' },
          { value: 'same',   label: 'Continue same size',  sub: 'Stay consistent' },
          { value: 'double', label: 'Double down',         sub: 'Recover losses fast' },
        ],
      },
    ];

    const TOTAL = CARDS.length;
    const step = riskStep ?? 0;
    const setStep = setRiskStep;
    const saving = riskStepSaving;
    const setSaving = setRiskStepSaving;


    const handleSelect = async (field, value) => {
      const newForm = { ...riskForm, [field]: value };
      setRiskForm(newForm);

      const isLastQ = step === TOTAL - 1;

      // Save on every answer so partial progress is never lost
      setSaving(true);
      try {
        const payload = Object.fromEntries(
          Object.entries(newForm).filter(([, v]) => v !== '')
        );
        if (typeof payload.dependents !== 'number') payload.dependents = parseInt(payload.dependents) || 0;
        const res = await axios.put(`${API_URL}/auth/profile`, payload);
        setRiskProfile(res.data);

        if (isLastQ || riskReturnTo != null) {
          const dest = riskReturnTo || 'score';
          setRiskReturnTo(null);
          setStep(dest);
          setSaving(false);
          // Unlock the app gate — questionnaire is now complete
          if (res.data.questionnaire_completed) {
            const isFirstTime = !localStorage.getItem('tr_journey_seen');
            setUser(prev => prev ? { ...prev, questionnaire_completed: true } : prev);
            if (isFirstTime) {
              localStorage.setItem('tr_journey_seen', '1');
              navigate('/journey');
              return;
            }
          }
        } else {
          // Keep saving=true until the step actually advances — prevents a
          // second click from queuing another +1 before the first fires.
          setTimeout(() => {
            setStep(s => typeof s === 'number' ? s + 1 : s);
            setSaving(false);
          }, 260);
        }
      } catch {
        setSaving(false);
        import('react-hot-toast').then(m => m.default.error('Failed to save'));
      }
    };

    const scoreColor = !riskProfile?.risk_score ? colors.accent
      : riskProfile.risk_score >= 66 ? '#ef4444'
      : riskProfile.risk_score >= 46 ? '#f97316'
      : riskProfile.risk_score >= 26 ? colors.accent
      : '#16C784';

    const Header = () => {
      const handleBack = () => {
        if (riskReturnTo) { setRiskReturnTo(null); setStep(riskReturnTo); }
        else if (step === 'answers') setStep('score');
        else if (step === 'score' || step === 0) onBack();
        else setStep(s => s - 1);
      };
      return (
        <div>
          {locked && (
            <div className="mb-3 px-3 py-2.5 rounded-xl flex items-center gap-2"
              style={{ background: `${colors.accent}15`, border: `1px solid ${colors.accent}30` }}>
              <ShieldAlert className="w-4 h-4 flex-shrink-0" style={{ color: colors.accent }} />
              <p className="text-[12px] font-medium" style={{ color: colors.primaryText }}>
                Complete your risk profile to start using TradeReflex
              </p>
            </div>
          )}
          <div className="flex items-center space-x-3 mb-3">
            {!locked && (
              <button onClick={handleBack} className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-[13px] font-semibold"
                style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, color: colors.secondaryText, borderWidth: '1px', borderColor: colors.accentBorder }}>
                <ArrowLeft className="w-3.5 h-3.5" />
                {typeof step === 'number' && step > 0 ? 'Previous' : 'Back'}
              </button>
            )}
            <h1 className="text-[14px] font-bold flex-1" style={{ color: colors.primaryText }}>Trading Risk Profile</h1>
            {!locked && typeof step === 'number' && step < TOTAL - 1 && (
              <button onClick={() => setStep(s => s + 1)}
                className="flex items-center gap-1 px-3 py-1 rounded-lg text-[13px] font-semibold"
                style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, color: colors.accent, borderWidth: '1px', borderColor: colors.accent }}>
                Skip
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {typeof step === 'number' && (
            <div>
              <div className="flex justify-between text-[13px] mb-1.5">
                <span style={{ color: colors.accent }}>{CARDS[step]?.category} · {CARDS[step]?.step}</span>
                <span style={{ color: colors.secondaryText }}>{step + 1} / {TOTAL}</span>
              </div>
              <div className="w-full rounded-full h-1" style={{ backgroundColor: colors.border }}>
                <div className="h-1 rounded-full transition-all duration-300"
                  style={{ width: `${((step + 1) / TOTAL) * 100}%`, backgroundColor: colors.accent }} />
              </div>
            </div>
          )}
        </div>
      );
    };

    // ── Question card ─────────────────────────────────────────────────────
    if (typeof step === 'number') {
      const card = CARDS[step];
      const currentVal = riskForm[card.field];
      return (
        <div className="space-y-5">
          <Header />
          <p className="text-[17px] font-bold leading-snug" style={{ color: colors.primaryText }}>
            {card.question}
          </p>
          <div className="space-y-2.5">
            {card.options.map(opt => {
              // eslint-disable-next-line eqeqeq
              const selected = currentVal == opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => handleSelect(card.field, opt.value)}
                  disabled={saving}
                  className="w-full text-left p-1.5 rounded-xl transition-all active:scale-[0.98]"
                  style={{
                    backgroundColor: selected ? `${colors.accent}18` : colors.cardBg,
                    borderWidth: '1.5px',
                    borderColor: selected ? colors.accent : colors.border,
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[14px] font-semibold" style={{ color: selected ? colors.accent : colors.primaryText }}>
                        {opt.label}
                      </p>
                      {opt.sub && (
                        <p className="text-[14px] mt-0.5" style={{ color: colors.secondaryText }}>{opt.sub}</p>
                      )}
                    </div>
                    <div className="w-5 h-5 rounded-full flex-shrink-0 ml-3 flex items-center justify-center transition-all"
                      style={{ backgroundColor: selected ? colors.accent : 'transparent', borderWidth: '2px', borderColor: selected ? colors.accent : colors.border }}>
                      {selected && (
                        <svg viewBox="0 0 10 8" className="w-2.5 h-2.5">
                          <path d="M1 4l2.5 2.5L9 1" stroke="white" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          {saving && <p className="text-[13px] text-center" style={{ color: colors.secondaryText }}>Saving…</p>}
        </div>
      );
    }

    // ── Score card ────────────────────────────────────────────────────────
    if (step === 'score') {
      return (
        <div className="space-y-4">
          <Header />

          <div data-tour="risk-score-card" className="app-card rounded-2xl p-1.5 text-center"
            style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderWidth: '1px', borderColor: colors.accentBorder }}>
            {/* Edit Answers — top right */}
            <div className="flex justify-end mb-2">
              <button onClick={() => setStep('answers')}
                className="px-3 py-1 rounded-lg text-[14px] font-semibold border transition-all"
                style={{ borderColor: colors.accent, color: colors.accent, backgroundColor: `${colors.accent}10` }}>
                Edit Answers
              </button>
            </div>
            <p className="text-[13px] uppercase tracking-wider mb-3" style={{ color: colors.secondaryText }}>Your Risk Score</p>
            <p className="text-7xl font-black mb-1 tabular-nums" style={{ color: scoreColor }}>{riskProfile?.risk_score}</p>
            <p className="text-[14px] mb-3" style={{ color: colors.secondaryText }}>out of 100</p>
            {riskProfile?.risk_profile_type && (
              <span className="inline-block px-1.5 py-1 rounded-full text-[14px] font-bold"
                style={{ backgroundColor: `${scoreColor}20`, color: scoreColor }}>
                {riskProfile.risk_profile_type}
              </span>
            )}

            {/* 5-band spectrum bar */}
            <div className="mt-5 px-1.5 text-left">
              <div className="relative h-2 rounded-full"
                style={{ background: 'linear-gradient(to right, #16C784 0%, #16C784 25%, #84cc16 35%, #f59e0b 50%, #f97316 65%, #ef4444 80%, #ef4444 100%)' }}>
                <div className="absolute w-4 h-4 rounded-full bg-white shadow border-2"
                  style={{
                    left: `${Math.min(Math.max(riskProfile?.risk_score ?? 0, 2), 97)}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    borderColor: scoreColor,
                  }} />
              </div>
              <div className="flex justify-between mt-2">
                {['V.Cons.', 'Cons.', 'Moderate', 'Aggr.', 'V.Aggr.'].map(l => (
                  <span key={l} className="text-[11px]" style={{ color: colors.secondaryText }}>{l}</span>
                ))}
              </div>
            </div>
          </div>

          {riskProfile?.capacity_score != null && (
            <div data-tour="risk-score-breakdown" className="app-card rounded-2xl p-1.5 space-y-3.5"
              style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderWidth: '1px', borderColor: colors.accentBorder }}>
              <p className="text-[13px] font-semibold uppercase tracking-wide" style={{ color: colors.secondaryText }}>Score Breakdown</p>
              {[
                ['Financial Capacity', riskProfile.capacity_score, 30, colors.accent],
                ['Knowledge',          riskProfile.knowledge_score, 20, '#a855f7'],
                ['Behavior',           riskProfile.behavior_score,  50, '#f97316'],
              ].map(([label, val, max, color]) => (
                <div key={label}>
                  <div className="flex justify-between text-[13px] mb-1.5">
                    <span style={{ color: colors.primaryText }}>{label}</span>
                    <span className="font-semibold" style={{ color }}>{val} <span style={{ color: colors.secondaryText }}>/ {max}</span></span>
                  </div>
                  <div className="w-full rounded-full h-2" style={{ backgroundColor: colors.border }}>
                    <div className="h-2 rounded-full transition-all duration-500"
                      style={{ width: `${(val / max) * 100}%`, backgroundColor: color }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          <div data-tour="risk-recommendations" className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {[
              { label: 'Risk / Trade',      value: riskProfile?.recommended_risk_percent != null ? `${riskProfile.recommended_risk_percent}%` : '—', color: '#16C784' },
              { label: 'Style',             value: (riskProfile?.recommended_trade_style || '—').split(' / ')[0], color: colors.accent },
              { label: 'Max Trades / Day',  value: riskProfile?.recommended_max_trades != null ? `${riskProfile.recommended_max_trades}` : '—', color: '#f97316' },
              { label: 'Daily Loss Limit',  value: riskProfile?.max_daily_loss_pct != null ? `${riskProfile.max_daily_loss_pct}%` : '—', color: '#ef4444' },
              { label: 'Options Allocation',value: riskProfile?.options_allocation_pct != null ? `${riskProfile.options_allocation_pct}%` : '—', color: '#a78bfa' },
            ].map(({ label, value, color }) => (
              <div key={label} className="app-card rounded-xl p-1.5 text-center relative"
                style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderWidth: '1px', borderColor: colors.accentBorder }}>
                {label === 'Style' && <StyleInfoTooltip colors={colors} />}
                <p className="text-[11px] mb-1" style={{ color: colors.secondaryText }}>{label}</p>
                <p className="text-[14px] font-bold" style={{ color }}>{value}</p>
              </div>
            ))}
          </div>

          {/* Learn about risk management */}
          <button
            onClick={() => navigate('/learning?view=level&level=2&card=0')}
            className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-left"
            style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, border: `1px solid ${colors.accent}28` }}>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" style={{ color: colors.accent }} />
              <span className="text-[11px] font-medium" style={{ color: 'rgba(255,255,255,0.55)' }}>
                Why does this matter? <span style={{ color: colors.accent }}>Learn to protect your capital</span>
              </span>
            </div>
            <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" style={{ color: `${colors.accent}50` }} />
          </button>

          {(!riskProfile?.questionnaire_completed || riskProfile?.recommended_risk_percent == null) && (
            <div className="flex items-center gap-3 px-3 py-1 rounded-xl border"
              style={{ backgroundColor: `${colors.accent}10`, borderColor: `${colors.accent}40` }}>
              <span className="text-[14px]">📋</span>
              <div className="flex-1">
                <p className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>Complete your risk assessment</p>
                <p className="text-[13px]" style={{ color: colors.secondaryText }}>Answer 13 questions to get your personalised risk score and trading recommendations.</p>
              </div>
              <button onClick={() => setRiskStep(0)}
                className="flex-shrink-0 px-3 py-1 rounded-lg text-[14px] font-semibold"
                style={{ backgroundColor: colors.accent, color: '#fff' }}>
                Start
              </button>
            </div>
          )}

          <div data-tour="risk-position-sizer"><PositionSizer riskProfile={riskProfile} user={user} colors={colors} /></div>

          {/* Risk Adherence Section */}
          {adherence && (
            <div data-tour="risk-adherence" className="app-card rounded-2xl p-1.5 space-y-3"
              style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderWidth: '1px', borderColor: colors.accentBorder }}>
              <div className="flex items-center justify-between">
                <p className="text-[13px] font-semibold uppercase tracking-wide" style={{ color: colors.secondaryText }}>
                  Risk Adherence
                </p>
                {adherence.adherence?.total_evaluated > 0
                  ? (adherence.adherence.violations === 0
                    ? <ShieldCheck className="w-4 h-4" style={{ color: '#16C784' }} />
                    : <ShieldAlert className="w-4 h-4" style={{ color: '#f97316' }} />)
                  : null}
              </div>

              {adherence.adherence?.total_evaluated === 0 ? (
                <p className="text-[13px]" style={{ color: colors.secondaryText }}>
                  No trades with stop losses yet — place a trade with an SL to track adherence.
                </p>
              ) : (
                <>
                  {/* Score bar */}
                  <div>
                    <div className="flex justify-between text-[13px] mb-1.5">
                      <span style={{ color: colors.primaryText }}>Following your risk limit</span>
                      <span className="font-bold" style={{ color: adherence.adherence.score >= 70 ? '#16C784' : adherence.adherence.score >= 40 ? '#f97316' : '#ef4444' }}>
                        {adherence.adherence.score}/100
                      </span>
                    </div>
                    <div className="w-full rounded-full h-2" style={{ backgroundColor: colors.border }}>
                      <div className="h-2 rounded-full transition-all duration-500"
                        style={{
                          width: `${adherence.adherence.score}%`,
                          backgroundColor: adherence.adherence.score >= 70 ? '#16C784' : adherence.adherence.score >= 40 ? '#f97316' : '#ef4444',
                        }} />
                    </div>
                  </div>

                  {/* Stats row */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1">
                    {[
                      { label: 'Trades Checked', value: adherence.adherence.total_evaluated },
                      { label: 'Within Limit',   value: adherence.adherence.clean_trades, color: '#16C784' },
                      { label: 'Violations',      value: adherence.adherence.violations,   color: adherence.adherence.violations > 0 ? '#f97316' : colors.secondaryText },
                    ].map(({ label, value, color }) => (
                      <div key={label} className="text-center rounded-xl p-2"
                        style={{ backgroundColor: colors.inputBg }}>
                        <p className="text-[18px] font-bold" style={{ color: color || colors.primaryText }}>{value}</p>
                        <p className="text-[11px] mt-0.5" style={{ color: colors.secondaryText }}>{label}</p>
                      </div>
                    ))}
                  </div>

                  {/* Per-trade violations */}
                  {adherence.violations?.length > 0 && (
                    <div className="pt-1 space-y-1.5" style={{ borderTop: `1px solid ${colors.border}` }}>
                      <p className="text-[12px] font-semibold uppercase tracking-wide" style={{ color: colors.secondaryText }}>
                        Breaches
                      </p>
                      {adherence.violations.map((v, i) => (
                        <div key={i} className="flex items-center justify-between rounded-lg px-2.5 py-1.5"
                          style={{ backgroundColor: v.severity === 'severe' ? '#ef444412' : '#f9731612', borderWidth: '1px', borderColor: v.severity === 'severe' ? '#ef444430' : '#f9731630' }}>
                          <div>
                            <span className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>{v.symbol || '—'}</span>
                            {v.date && <span className="text-[12px] ml-2" style={{ color: colors.secondaryText }}>{v.date}</span>}
                          </div>
                          <div className="text-right">
                            <span className="text-[13px] font-bold" style={{ color: v.severity === 'severe' ? '#ef4444' : '#f97316' }}>
                              {v.actual_loss_pct}%
                            </span>
                            <span className="text-[12px] ml-1" style={{ color: colors.secondaryText }}>/ {v.limit_pct}% limit</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {adherence.adherence.violations === 0 && (
                    <p className="text-[13px]" style={{ color: '#16C784' }}>
                      All {adherence.adherence.total_evaluated} evaluated trades stayed within your {adherence.profile?.recommended_risk_percent}% limit.
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {/* Trade Calculator */}
          <button
            onClick={() => navigate('/calculator')}
            className="w-full flex items-center justify-between px-4 py-3.5 rounded-xl"
            style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`,
                     border: `1px solid ${colors.accent}28` }}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: `${colors.accent}22`, border: `1px solid ${colors.accent}38` }}>
                <Calculator className="w-4 h-4" style={{ color: colors.accent }} />
              </div>
              <div className="text-left">
                <p className="text-sm font-bold" style={{ color: colors.accent }}>Trade Calculator</p>
                <p className="text-[11px] mt-0.5" style={{ color: colors.secondaryText }}>
                  Plan equity & F&O trades with your risk profile
                </p>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: colors.accent }} />
          </button>

        </div>
      );
    }

    // ── Answers sheet — inline edit all 13 answers ────────────────────────
    if (step === 'answers') {
      const handleInlineSelect = async (field, value) => {
        const newForm = { ...riskForm, [field]: value };
        setRiskForm(newForm);
        setSaving(true);
        try {
          const payload = { ...newForm };
          if (typeof payload.dependents !== 'number') payload.dependents = parseInt(payload.dependents) || 0;
          const res = await axios.put(`${API_URL}/auth/profile`, payload);
          setRiskProfile(res.data);
        } catch {
          import('react-hot-toast').then(m => m.default.error('Failed to save'));
        } finally {
          setSaving(false);
        }
      };

      const sections = [
        { title: 'Financial Capacity', indices: [0, 1, 2, 3, 4] },
        { title: 'Knowledge & Experience', indices: [5, 6] },
        { title: 'Behavioral Traits', indices: [7, 8, 9, 10, 11, 12] },
      ];

      return (
        <div className="space-y-5">
          <Header />
          <div className="flex items-center justify-between">
            <p className="text-[13px]" style={{ color: colors.secondaryText }}>
              Tap any option to update it. Saves instantly.
            </p>
            {saving && <span className="text-[13px]" style={{ color: colors.accent }}>Saving…</span>}
          </div>

          {sections.map(({ title, indices }) => (
            <div key={title} className="space-y-3">
              <p className="text-[13px] font-semibold uppercase tracking-wider" style={{ color: colors.secondaryText }}>{title}</p>
              {indices.map(idx => {
                const card = CARDS[idx];
                const currentVal = riskForm[card.field];
                return (
                  <div key={card.field} className="rounded-xl p-3"
                    style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderWidth: '1px', borderColor: colors.accentBorder }}>
                    <p className="text-[13px] font-semibold mb-2.5" style={{ color: colors.primaryText }}>{card.question}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {card.options.map(opt => {
                        // eslint-disable-next-line eqeqeq
                        const selected = currentVal == opt.value;
                        return (
                          <button
                            key={opt.value}
                            onClick={() => handleInlineSelect(card.field, opt.value)}
                            disabled={saving}
                            className="px-3 py-1 rounded-lg text-[14px] font-semibold transition-all active:scale-95"
                            style={{
                              backgroundColor: selected ? `${colors.accent}20` : `${colors.border}40`,
                              borderWidth: '1.5px',
                              borderColor: selected ? colors.accent : 'transparent',
                              color: selected ? colors.accent : colors.secondaryText,
                            }}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          <button onClick={() => setStep('score')}
            className="w-full py-1 rounded-xl text-[14px] font-medium"
            style={{ background: `linear-gradient(to right, ${colors.accent}, #0d9488)`, color: '#fff' }}>
            Done — Back to Score
          </button>
        </div>
      );
    }

    return null;
}
